import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import { useToast } from '@/hooks/use-toast';
import { AnswerQuestionField } from '../../components/answer-question-field/answer-question-field';
import {
  useGetSurveyFormLookup,
  useGetSurveySubmissionById,
  useSubmitSurvey,
} from '../../hooks/use-submissions';
import {
  AnswerValue,
  getFormQuestions,
  isDeadlineExpired,
  SurveyFormLookup,
  SurveySubmission,
  SurveySubmissionsPagingResult,
} from '../../types/submission.types';
import { FormPageSkeleton, PageHeader } from '@/components/core';
import { APP_PATHS } from '@/constant/app-paths';

export const SubmissionAnswerPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { submissionId } = useParams<{ submissionId: string }>();

  const { data: submissionData, isLoading: isLoadingSubmission } =
    useGetSurveySubmissionById(submissionId);
  const submission: SurveySubmission | undefined = (
    submissionData as {
      SurveySubmissions?: SurveySubmissionsPagingResult;
    }
  )?.SurveySubmissions?.items?.[0];

  const { data: formData, isLoading: isLoadingForm } = useGetSurveyFormLookup(
    submission?.SurveyFormId
  );
  const form = (formData as { SurveyForms?: { items: SurveyFormLookup[] } })?.SurveyForms
    ?.items?.[0];
  const questions = getFormQuestions(form);

  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});

  useEffect(() => {
    if (!submission?.AnswersJson) return;
    try {
      const parsed = JSON.parse(submission.AnswersJson) as AnswerValue[];
      setAnswers(Object.fromEntries(parsed.map((answer) => [answer.QuestionId, answer])));
    } catch {
      setAnswers({});
    }
  }, [submission]);

  const { mutateAsync: submitSurvey, isPending } = useSubmitSurvey();

  const isReadOnly = submission?.Status === 'Completed';
  const deadlineExpired = isDeadlineExpired(submission?.Deadline);

  const handleSubmit = async () => {
    // Without the form there are no questions to check, and the answers would go out empty.
    if (!submission || !form) return;
    if (deadlineExpired) {
      toast({ title: t('SURVEY_DEADLINE_HAS_EXPIRED'), variant: 'destructive' });
      return;
    }
    const missingRequired = questions.some((question) => {
      if (!question.IsRequired) return false;
      const value = answers[question.QuestionId];
      if (!value) return true;
      if (question.FormatType === 'multiple-choice') return !value.McOptionsValue;
      if (question.FormatType === 'checkboxes') return !value.CbOptionsValues?.length;
      if (question.FormatType === 'rating' || question.FormatType === 'nps')
        return value.Rating === undefined;
      return !value.Answer;
    });
    if (missingRequired) {
      toast({ title: t('PLEASE_ANSWER_ALL_REQUIRED_QUESTIONS'), variant: 'destructive' });
      return;
    }

    try {
      await submitSurvey({
        itemId: submission.ItemId,
        answers: questions.map(
          (question) => answers[question.QuestionId] ?? { QuestionId: question.QuestionId }
        ),
      });
      navigate(APP_PATHS.MY_SURVEY_RESPONSES);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  // The questions come with the form: until then no title, no questions and no Submit.
  if (isLoadingSubmission || isLoadingForm) return <FormPageSkeleton />;
  if (!submission) {
    return <p className="text-sm text-muted-foreground">{t('NO_DATA_FOUND')}</p>;
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <PageHeader title={form?.Title ?? t('SURVEY')} />

      {!isReadOnly && deadlineExpired && (
        <p className="rounded-md border border-destructive bg-destructive-subtle p-3 text-sm text-destructive">
          {t('SURVEY_DEADLINE_HAS_EXPIRED')}
        </p>
      )}

      <div className="flex flex-col gap-4">
        {questions.map((question) => (
          <AnswerQuestionField
            key={question.QuestionId}
            question={question}
            value={answers[question.QuestionId] ?? { QuestionId: question.QuestionId }}
            onChange={(value) => setAnswers((prev) => ({ ...prev, [question.QuestionId]: value }))}
            readOnly={isReadOnly || deadlineExpired}
          />
        ))}
      </div>

      {form && !isReadOnly && !deadlineExpired && (
        <div className="flex justify-end">
          <Button onClick={handleSubmit} loading={isPending} disabled={isPending}>
            {t('SUBMIT')}
          </Button>
        </div>
      )}
    </div>
  );
};
